import React, { useCallback, useContext, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SessionContext from "../context/session";
import ChatInput from "../components/ChatInput";
import Chatbox from "../components/Chatbox";
import Sidebar, { RecentChat } from "../components/Sidebar";
import SidecarDrawer from "../components/SidecarDrawer";
import InlineNotification from "../components/InlineNotification";
import { apiUrl } from "../config/runtime";
import { normalizeChatMessages } from "../types/contracts";
import "./Chat.css";

const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const MODELS = [
  { id: "agnes2.5flash", name: "agnes2.5flash", desc: "Agnes 2.5 Flash 核心智能体模型" },
];

export const ChatPage: React.FC = () => {
  const [chat, setChat] = useState<any>({});
  const [error, setError] = useState<string>("");
  const [runtimeError, setRuntimeError] = useState<string>("");

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidecarOpen, setSidecarOpen] = useState(false);
  const [recentChats, setRecentChats] = useState<RecentChat[]>([]);

  const [selectedModel, setSelectedModel] = useState<string>("agnes2.5flash");
  const [modelMenuOpen, setModelMenuOpen] = useState(false);

  const params = useParams();
  const token_id = params.token_id;
  const navigate = useNavigate();

  useEffect(() => {
    // 运行错误只属于产生它的会话，切换会话后不应继续展示。
    setRuntimeError("");
  }, [token_id]);

  const {
    token,
    setToken,
    messages,
    setMessages,
    name,
    setName,
    socketState,
  } = useContext(SessionContext);

  const handleNewChat = useCallback(() => {
    fetch(apiUrl("/token?name=" + encodeURIComponent(name || "用户")), {
      method: "POST",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.token) {
          setToken(data.token);
          setMessages([]);
          setError("");
          navigate(`/chat/${data.token}`);
        }
      })
      .catch((err) => console.error("创建新对话失败:", err));
  }, [name, navigate, setMessages, setToken]);

  useEffect(() => {
    const userKey = name ? `recent_chats_${name}` : "recent_chats_default";
    try {
      const stored = localStorage.getItem(userKey);
      if (stored) {
        setRecentChats(JSON.parse(stored));
      } else {
        setRecentChats([]);
      }
    } catch {
      /* ignore */
    }
  }, [name]);

  useEffect(() => {
    const activeToken = token_id || token;
    if (activeToken && activeToken !== token) {
      setToken(activeToken);
    }
  }, [token_id, token, setToken]);

  useEffect(() => {
    const activeToken = token_id || token;
    if (!activeToken) return;

    fetch(apiUrl(`/refresh_token?token=${encodeURIComponent(activeToken)}`))
      .then((res) => {
        if (res.status === 404) {
          console.warn(`[Auto-Recovery] Token ${activeToken} 已过期或失效，自动初始化新会话`);
          handleNewChat();
          return null;
        }
        if (!res.ok) throw new Error("获取历史记录失败");
        return res.json();
      })
      .then((data) => {
        if (!data) return;
        if (data && data.name) setName(data.name);
        if (data && Array.isArray(data.messages)) {
          setMessages(normalizeChatMessages(data.messages));
        }
        setError("");

        if (data && data.token && Array.isArray(data.messages) && data.messages.length > 0) {
          const userName = data.name || name || "default";
          const userKey = `recent_chats_${userName}`;
          const firstUserMsg = data.messages.find((m: any) => m.role === "user");
          const title = firstUserMsg
            ? firstUserMsg.content.slice(0, 16)
            : `${userName} 的对话`;
          const ts = firstUserMsg?.created_at || new Date().toISOString();

          setRecentChats((prev) => {
            const exists = prev.some((c) => c.id === data.token);
            let updated: RecentChat[];
            if (exists) {
              updated = prev.map((c) =>
                c.id === data.token ? { ...c, name: title, timestamp: c.timestamp || ts } : c
              );
            } else {
              updated = [{ id: data.token, name: title, timestamp: ts }, ...prev];
            }
            localStorage.setItem(userKey, JSON.stringify(updated));
            return updated;
          });
        }
      })
      .catch((err) => {
        console.error("加载聊天历史异常:", err);
        setError("无法加载历史消息，请检查后端服务。");
      });
  }, [token, token_id, setMessages, setName, name, handleNewChat]);

  useEffect(() => {
    const activeToken = token_id || token;
    if (activeToken && messages.length > 0) {
      const userName = name || "default";
      const userKey = `recent_chats_${userName}`;
      const firstUserMsg = messages.find((m: any) => m.role === "user");
      const title = firstUserMsg
        ? firstUserMsg.content.slice(0, 16)
        : `${userName} 的对话`;
      const ts = firstUserMsg?.created_at || new Date().toISOString();

      setRecentChats((prev) => {
        const exists = prev.some((c) => c.id === activeToken);
        let updated: RecentChat[];
        if (exists) {
          updated = prev.map((c) =>
            c.id === activeToken ? { ...c, name: title, timestamp: c.timestamp || ts } : c
          );
        } else {
          updated = [{ id: activeToken, name: title, timestamp: ts }, ...prev];
        }
        localStorage.setItem(userKey, JSON.stringify(updated));
        return updated;
      });
    }
  }, [messages, token, token_id, name]);

  const handleLogout = () => {
    setToken("");
    setName("");
    setMessages([]);
    navigate("/");
  };

  const handleDeleteChat = (deleteId: string) => {
    const userName = name || "default";
    const userKey = `recent_chats_${userName}`;
    const updated = recentChats.filter((c) => c.id !== deleteId);
    setRecentChats(updated);
    localStorage.setItem(userKey, JSON.stringify(updated));

    if (token === deleteId || token_id === deleteId) {
      setMessages([]);
      if (updated.length > 0) {
        navigate(`/chat/${updated[0].id}`);
      } else {
        handleNewChat();
      }
    }
  };

  const currentModelObj = MODELS.find((m) => m.name === selectedModel) || MODELS[0];
  const isActive = socketState === "active";
  const isEmpty = messages.length === 0;
  const visibleError = runtimeError || error;

  return (
    <div className="chat-container">
      {visibleError ? (
        <div style={{ position: "fixed", top: "16px", left: "50%", transform: "translateX(-50%)", zIndex: 1000 }}>
          <InlineNotification kind="error">{visibleError}</InlineNotification>
        </div>
      ) : null}

      <Sidebar
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        name={name}
        recentChats={recentChats}
        activeId={token_id || token}
        onNewChat={handleNewChat}
        onLogout={handleLogout}
        onDeleteChat={handleDeleteChat}
      />

      <main className={`chat-main ${isEmpty ? "chat-main--empty" : "chat-main--active"}`}>
        <div className="chat__header-bar">
          <div className="model-selector-wrapper">
            <button
              type="button"
              className="model-selector-btn"
              onClick={() => setModelMenuOpen(!modelMenuOpen)}
            >
              <span className="model-selector-name">{currentModelObj.name}</span>
              <ChevronDownIcon />
            </button>

            {modelMenuOpen && (
              <>
                <div
                  className="model-selector-overlay"
                  onClick={() => setModelMenuOpen(false)}
                />
                <div className="model-selector-dropdown">
                  {MODELS.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      className={`model-option ${
                        selectedModel === m.name ? "model-option--active" : ""
                      }`}
                      onClick={() => {
                        setSelectedModel(m.name);
                        setModelMenuOpen(false);
                      }}
                    >
                      <div className="model-option-top">
                        <span className="model-option-title">{m.name}</span>
                        {selectedModel === m.name && (
                          <span className="model-option-check">✓</span>
                        )}
                      </div>
                      <div className="model-option-desc">{m.desc}</div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="chat__header-status">
            <span
              className={`chat__status-dot ${
                isActive ? "chat__status-dot--online" : "chat__status-dot--offline"
              }`}
            />
            <span className="chat__status-text">
              {isActive ? "系统就绪" : "连接中…"}
            </span>

            <button
              type="button"
              className="sidecar-toggle-btn"
              onClick={() => setSidecarOpen(!sidecarOpen)}
              title="切换右侧定位与成果面板"
            >
              定位面板
            </button>
          </div>
        </div>

        {/* 🟩 强对齐纯 Flex 视窗：在 isEmpty 时，标题与 ChatInput 一起由居中容器挂载，保持单例防止组件卸载销毁 state */}
        {isEmpty ? (
          <div className="chat-hero-container">
            <h1 className="chat-hero__title">需要我为你做些什么？</h1>
          </div>
        ) : (
          <Chatbox />
        )}

        <div className={`chat__composer ${isEmpty ? "chat__composer--hero" : "chat__composer--bottom"}`}>
          <ChatInput
            chat={chat}
            setChat={setChat}
            onControlError={setRuntimeError}
          />
        </div>
      </main>

      <SidecarDrawer
        isOpen={sidecarOpen}
        onClose={() => setSidecarOpen(false)}
        messages={messages}
      />
    </div>
  );
};

export default ChatPage;
